import { MIN_PASSWORD_LENGTH, SUPPORT_MESSAGE } from '../../shared/constants';
import React, { FormEvent, useEffect, useState } from 'react';
import { isBoolean, isEmpty } from 'lodash';

import AuthService from '../services/AuthService';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ErrorPanel from './ErrorPanel';
import Grid from '@mui/material/Unstable_Grid2';
import MessageBox from './MessageBox';
import PasswordField from './PasswordField';
import usePasswordUpdates from '../hooks/usePasswordUpdates';

interface ChangePasswordDialogProps {
  userkey: number;
  username: string;
  open: boolean;
  checkCurrentPassword: boolean;
  onClose: () => void;
}

const ChangePasswordDialog = (props: ChangePasswordDialogProps) => {
  const tenant = AuthService.Tenant;
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPasswordCorrect, setCurrentPasswordCorrect] = useState<boolean>(
    !props.checkCurrentPassword
  );
  // `editingPassword` is used to show/hide the error and helper text.
  // Whenever user is editing `Confirm Password` then hide the error/helper text.
  const [editingPassword, setEditingPassword] = useState<boolean>(
    !newPassword && !confirmPassword
  );

  const {
    auth,
    updatePassword,
    authData,
    updatePasswordError,
    updatePasswordData
  } = usePasswordUpdates();

  useEffect(() => {
    if (authData) {
      setCurrentPasswordCorrect(authData.auth);
      setEditingPassword(false);
    }
  }, [authData]);

  const arePasswordsMatch = () => {
    if (currentPasswordCorrect && newPassword && confirmPassword) {
      return newPassword === confirmPassword;
    }
  };

  const canSubmit = () => {
    if (currentPasswordCorrect) {
      return (
        arePasswordsMatch() &&
        newPassword.length >= MIN_PASSWORD_LENGTH && //Check for minimum password length
        confirmPassword.length >= MIN_PASSWORD_LENGTH
      );
    } else {
      return !isEmpty(currentPassword);
    }
  };

  const currentPasswordErrorMessage = () => {
    return !isEmpty(currentPassword) &&
      !currentPasswordCorrect &&
      !editingPassword
      ? 'Wrong password'
      : '';
  };

  const confirmPasswordErrorMessage = () => {
    return !arePasswordsMatch() && !editingPassword;
  };

  const hasNoMinimumLength = () => {
    return (
      !!newPassword &&
      newPassword.length < MIN_PASSWORD_LENGTH &&
      !editingPassword
    );
  };

  const close = () => {
    props.onClose();
  };

  const checkCurrentPassword = () => {
    auth(tenant, props.username, currentPassword);
  };

  const changePassword = () => {
    updatePassword(tenant, props.username, newPassword);
  };

  const handleOk = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    currentPasswordCorrect ? changePassword() : checkCurrentPassword();
  };

  return (
    <Dialog open={props.open} fullWidth maxWidth="xs">
      <DialogTitle>{`Update password for '${props.username}'`}</DialogTitle>
      <DialogContent>
        {updatePasswordData ? (
          <MessageBox
            header="Success!"
            message={`Password for '${props.username}' has been updated.`}
          />
        ) : updatePasswordError ? (
          <ErrorPanel
            title="Whoops!"
            message={`Updating password for '${props.username}' has failed.`}
            details={SUPPORT_MESSAGE}
          />
        ) : (
          <form id="password-update-form" onSubmit={handleOk}>
            <Grid container direction="column" spacing={1}>
              {props.checkCurrentPassword && (
                <Grid>
                  <PasswordField
                    label="Current password"
                    autofocus
                    value={currentPassword}
                    correct={currentPasswordCorrect}
                    error={currentPasswordErrorMessage()}
                    disabled={currentPasswordCorrect}
                    onFocus={() => setEditingPassword(true)}
                    onChange={setCurrentPassword}
                  />
                </Grid>
              )}
              {currentPasswordCorrect && (
                <>
                  <Grid>
                    <PasswordField
                      label="New password"
                      autofocus={currentPasswordCorrect}
                      onChange={setNewPassword}
                      onFocus={() => setEditingPassword(true)}
                      onBlur={() => setEditingPassword(false)}
                    />
                  </Grid>
                  <Grid>
                    <PasswordField
                      label="Confirm password"
                      disabled={isEmpty(newPassword) || hasNoMinimumLength()}
                      error={
                        hasNoMinimumLength()
                          ? `Password must be at least ${MIN_PASSWORD_LENGTH} characters long`
                          : confirmPasswordErrorMessage()
                          ? 'Passwords do not match'
                          : ''
                      }
                      onFocus={() => setEditingPassword(true)}
                      onChange={setConfirmPassword}
                      onBlur={() => setEditingPassword(false)}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        {updatePasswordError || updatePasswordData ? (
          <Button variant="outlined" color="primary" onClick={close}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="outlined" color="primary" onClick={close}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!canSubmit()}
              form="password-update-form"
              onClick={() => handleOk()}
            >
              {currentPasswordCorrect ? 'Update' : 'Continue'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
